// Spatial focus manager. A tiny registry-plus-geometry engine that
// lets us drive "focus" around a page using 2D-direction cues (d-pad,
// left stick) instead of linear DOM tab order.
//
// Design intent:
//   - One singleton registry. Any component can become focusable by
//     registering itself with an id, a getRect() callable, and the
//     actions to run on A (onActivate) / B (onBack).
//   - Scope stack so a modal can "push" its own focusable set on top
//     of the parent screen's. Only the top scope is navigable.
//   - No DOM coupling. The registry stores rects-on-demand, not nodes.
//     This keeps it trivially unit-testable.
//
// Public API:
//   register(id, entry)      -> unregister()
//   setFocus(id)
//   getFocus()
//   moveFocus(direction)
//   activateFocus()
//   back()
//   pushScope(scopeId, { onBack })  -> popScope()
//   subscribe(fn)            -> unsubscribe()

const DIRECTIONS = new Set(['up', 'down', 'left', 'right']);
const MOVE_COOLDOWN_MS = 120;

function scoreCandidate(current, cand, direction) {
  const cx = cand.cx - current.cx;
  const cy = cand.cy - current.cy;
  switch (direction) {
    case 'right': return cx <= 0 ? Infinity : cx + 2 * Math.abs(cy);
    case 'left':  return cx >= 0 ? Infinity : -cx + 2 * Math.abs(cy);
    case 'down':  return cy <= 0 ? Infinity : cy + 2 * Math.abs(cx);
    case 'up':    return cy >= 0 ? Infinity : -cy + 2 * Math.abs(cx);
    default: return Infinity;
  }
}

function rectCenter(rect) {
  return { cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2 };
}

export function createSpatialFocus({
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
} = {}) {
  // entries: Map<id, { id, screenId, priority, getRect, onActivate, onBack }>
  const entries = new Map();
  // scope stack: array of { scopeId, onBack }. The implicit base scope
  // is '__root__' with no onBack.
  const scopes = [{ scopeId: '__root__', onBack: null }];
  const listeners = new Set();
  let focused = null;        // id of currently focused element
  let lastMoveAt = -Infinity;

  function currentScope() {
    return scopes[scopes.length - 1].scopeId;
  }

  function entriesInCurrentScope() {
    const scope = currentScope();
    const out = [];
    for (const e of entries.values()) if (e.screenId === scope) out.push(e);
    return out;
  }

  function notify() {
    for (const fn of listeners) fn(focused);
  }

  function ensureFocus() {
    // If the currently-focused id isn't valid in the top scope, clear.
    if (!focused) return;
    const e = entries.get(focused);
    if (!e || e.screenId !== currentScope()) focused = null;
  }

  function seedFirst() {
    const pool = entriesInCurrentScope();
    if (pool.length === 0) { focused = null; return; }
    pool.sort((a, b) => (b.priority - a.priority) || a.id.localeCompare(b.id));
    focused = pool[0].id;
  }

  function register(id, entry) {
    const e = {
      id,
      screenId: entry.screenId || currentScope(),
      priority: entry.priority ?? 0,
      getRect: entry.getRect,
      onActivate: entry.onActivate || null,
      onBack: entry.onBack || null,
    };
    entries.set(id, e);
    // First focusable in the current scope auto-focuses.
    if (!focused && e.screenId === currentScope()) {
      focused = id;
      notify();
    }
    return () => {
      entries.delete(id);
      if (focused === id) { focused = null; notify(); }
    };
  }

  function setFocus(id) {
    if (!entries.has(id)) return false;
    focused = id;
    notify();
    return true;
  }

  function getFocus() {
    ensureFocus();
    return focused;
  }

  function moveFocus(direction) {
    if (!DIRECTIONS.has(direction)) return false;
    const t = now();
    if (t - lastMoveAt < MOVE_COOLDOWN_MS) return false;
    lastMoveAt = t;

    ensureFocus();
    const pool = entriesInCurrentScope();
    if (pool.length === 0) return false;

    if (!focused) {
      // Nothing focused yet. Seed and report.
      seedFirst();
      notify();
      return Boolean(focused);
    }

    const currentEntry = entries.get(focused);
    if (!currentEntry) return false;
    const currentRect = currentEntry.getRect();
    if (!currentRect) return false;
    const current = rectCenter(currentRect);

    let bestId = null;
    let bestScore = Infinity;
    for (const cand of pool) {
      if (cand.id === focused) continue;
      const rect = cand.getRect();
      if (!rect) continue;
      const centre = rectCenter(rect);
      const s = scoreCandidate(current, centre, direction);
      if (!Number.isFinite(s)) continue;
      if (s < bestScore || (s === bestScore && cand.priority > (entries.get(bestId)?.priority ?? -Infinity))) {
        bestScore = s;
        bestId = cand.id;
      }
    }
    if (bestId === null) return false;
    focused = bestId;
    notify();
    return true;
  }

  function activateFocus() {
    ensureFocus();
    if (!focused) return false;
    const e = entries.get(focused);
    if (!e || !e.onActivate) return false;
    try { e.onActivate(); } catch (_) {}
    return true;
  }

  function back() {
    ensureFocus();
    if (focused) {
      const e = entries.get(focused);
      if (e && e.onBack) { try { e.onBack(); } catch (_) {} return true; }
    }
    const scope = scopes[scopes.length - 1];
    if (scope && scope.onBack) { try { scope.onBack(); } catch (_) {} return true; }
    return false;
  }

  function pushScope(scopeId, { onBack } = {}) {
    scopes.push({ scopeId, onBack: onBack || null });
    focused = null;
    seedFirst();
    notify();
    return () => {
      // Pop only if the top-of-stack scope matches; a mismatched pop
      // would indicate a mount/unmount ordering bug in a caller and
      // we'd rather leave state sane than silently remove the wrong
      // frame.
      const top = scopes[scopes.length - 1];
      if (top && top.scopeId === scopeId) scopes.pop();
      focused = null;
      seedFirst();
      notify();
    };
  }

  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  // Test hook only; runtime code must not rely on internal state.
  function _dumpForTest() {
    return {
      focused,
      scope: currentScope(),
      scopes: scopes.map((s) => s.scopeId),
      count: entries.size,
    };
  }

  return {
    register,
    setFocus,
    getFocus,
    moveFocus,
    activateFocus,
    back,
    pushScope,
    subscribe,
    _dumpForTest,
  };
}

export const spatialFocus = createSpatialFocus();
